'use client';

import React from 'react';
import {
    Button,
    Checkbox, Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";
import MarkdownEditor from "@/app/features/shared/editable-markdown/MarkdownEditor";
import { FormField, FormState } from "@/app/features/shared/form-builder/types/types";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ZodIssue } from "zod";
import { Cancel } from "@mui/icons-material";
import FormSubmitButton from "@/app/__to_be_integrated/_____components/_NEW/forms/buttons/FormSubmitButton";
import ErrorBox from "@/app/__to_be_integrated/_____components/_NEW/ErrorBox";

interface FormBuilderProps {
    purpose?: 'save' | 'send';
    fields: FormField[];
    formData: { [key: string]: any };
    formState: FormState;
    setFormData: (data: { [key: string]: any }) => void;
    onSubmit?: (closeOnSuccess: boolean) => void;
    disableSaveAndClose?: boolean;
    onCancel?: () => void;
}

/**
 * @deprecated
 */
export default function FormBuilder({
    fields,
    formData,
    formState,
    setFormData,
    onSubmit,
    onCancel,
    purpose = 'save',
    disableSaveAndClose = true,
}: FormBuilderProps) {

    const [touched, setTouched] = React.useState<Record<string, boolean>>({});

    /* eslint-disable @typescript-eslint/no-explicit-any */
    const handleChange = (name: string, value: any) => {
        setTouched((prev) => ({ ...prev, [name]: true }));
        setFormData({ ...formData, [name]: value });
    };

    const groupedFields = fields.reduce((acc, field) => {
        if (field.group) {
            if (!acc[field.group]) {
                acc[field.group] = [];
            }
            acc[field.group].push(field);
        } else {
            acc[field.name] = [field];
        }
        return acc;
    }, {} as { [key: string]: FormField[] });

    const dateValue = (fieldName: string) => {
        if (!formData[fieldName] || formData[fieldName] == '') {
            return null;
        }

        const dateTime = DateTime.fromISO(formData[fieldName])
        return dateTime.isValid ? dateTime : null;
    }

    function getFieldError(fieldName: string): string | null {
        if (formState.fieldErrors && typeof formState.fieldErrors === 'object') {
            if (formState.fieldErrors as { [key: string]: string | string[] }) {
                const fieldErrors = formState.fieldErrors as { [key: string]: string | string[] };

                if (fieldErrors[fieldName] as string) {
                    return fieldErrors[fieldName] as string;
                } else if (fieldErrors[fieldName] as string[]) {
                    return (fieldErrors[fieldName] as string[]).join('\n');
                }
            } else if (formState.fieldErrors as ZodIssue) {
                const fieldErrors = formState.fieldErrors as ZodIssue;
                return fieldErrors.path.includes(fieldName) ? fieldErrors.message : null;
            } else if (formState.fieldErrors as ZodIssue[]) {
                const fieldErrors = formState.fieldErrors as ZodIssue[];
                return fieldErrors.find((error) => error.path.includes(fieldName))?.message || null;
            }
        }
        return null;
    }

    function shouldShowError(field: FormField): boolean {
        const val = (formData as any)[field.name];
        const isString = typeof val === 'string';
        const hasSomeValue = isString ? val.trim() !== '' : false;
        return !!touched[field.name] || hasSomeValue;
    }

    return (
        <form className="w-full">
            <Stack spacing={2}>
                {Object.keys(groupedFields).map((groupKey) => (
                    <Stack
                        key={groupKey}
                        direction={groupedFields[groupKey].length > 1 ? 'row' : 'column'}
                        spacing={2}
                        justifyContent="center"
                    >
                        {groupedFields[groupKey].map((field, index) => {
                            switch (field.type) {
                                case 'hidden':
                                    return (
                                        <input
                                            key={`${field.name}-${index}`}
                                            name={field.name}
                                            type="hidden"
                                            value={formData[field.name] || ''}
                                        />
                                    );
                                case 'text':
                                    return (
                                        <Stack
                                            key={`${field.name}-${index}`}
                                            spacing={1}
                                        >
                                            <TextField
                                                label={field.label}
                                                size="small"
                                                variant="standard"
                                                value={formData[field.name] || ''}
                                                onChange={(e) =>
                                                    handleChange(field.name, e.target.value)
                                                }
                                                error={shouldShowError(field) && !!getFieldError(field.name)}
                                                helperText={shouldShowError(field) ? getFieldError(field.name) : ''}
                                                required={field.required}
                                                disabled={field.disabled || formState.isSubmitting}
                                                fullWidth
                                            />
                                            {field.description && (
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    justifySelf="center"
                                                    sx={{ marginBottom: '8px' }}
                                                >
                                                    <InfoOutlinedIcon sx={{ alignSelf: 'center' }}/>
                                                    <Typography
                                                        variant="caption"
                                                        fontSize="1rem"
                                                    >
                                                        {field.description}
                                                    </Typography>
                                                </Stack>
                                            )}
                                        </Stack>
                                    );
                                case 'rich-text':
                                    return (
                                        <Stack
                                            key={`${field.name}-${index}`}
                                            spacing={1}
                                        >
                                            <MarkdownEditor
                                                disabled={field.disabled || formState.isSubmitting}
                                                label={field.label}
                                                value={formData[field.name] || ''}
                                                error={shouldShowError(field) && !!getFieldError(field.name)}
                                                helperText={shouldShowError(field) ? (getFieldError(field.name) || '') : ''}
                                                onChange={(value) =>
                                                    handleChange(field.name, value)
                                                }
                                            />
                                            {field.description && (
                                                <Stack direction="row" spacing={1} justifySelf="center"
                                                       sx={{ marginBottom: '8px' }}>
                                                    <InfoOutlinedIcon sx={{ alignSelf: 'center' }}/>
                                                    <Typography variant="caption"
                                                                fontSize="1rem">{field.description}</Typography>
                                                </Stack>
                                            )}
                                        </Stack>
                                    );
                                case 'select':
                                    return (
                                        <FormControl
                                            key={`${field.name}-${index}`}
                                            fullWidth
                                        >
                                            <InputLabel>{field.label}</InputLabel>
                                            <Select
                                                variant="outlined"
                                                value={formData[field.name] || ''}
                                                onChange={(e) =>
                                                    handleChange(field.name, e.target.value)
                                                }
                                                required={field.required}
                                                disabled={field.disabled || formState.isSubmitting}
                                            >
                                                {field.options?.map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    );
                                case 'date':
                                    return (
                                        <LocalizationProvider
                                            key={`${field.name}-${index}`}
                                            dateAdapter={AdapterLuxon}
                                        >
                                            <DatePicker
                                                className="w-full"
                                                label={field.label}
                                                value={dateValue(field.name)}
                                                minDate={DateTime.fromObject({ year: 1800 })}
                                                disabled={field.disabled || formState.isSubmitting}
                                                onChange={(date) =>
                                                    handleChange(field.name, date)
                                                }
                                                slotProps={{
                                                    textField: {
                                                        required: field.required,
                                                        disabled: field.disabled || formState.isSubmitting
                                                    }
                                                }}
                                            />
                                        </LocalizationProvider>
                                    );
                                case 'decimal':
                                    return (
                                        <TextField
                                            key={`${field.name}-${index}`}
                                            label={field.label}
                                            type="decimal"
                                            value={formData[field.name] || ''}
                                            onChange={(e) =>
                                                handleChange(field.name, e.target.value)
                                            }
                                            required={field.required}
                                            disabled={field.disabled || formState.isSubmitting}
                                            fullWidth
                                        />
                                    );
                                case 'boolean':
                                    return (
                                        <Stack key={`${field.name}-${index}`} direction="row">
                                            <Switch
                                                checked={formData[field.name] || false}
                                                onChange={(e) =>
                                                    handleChange(field.name, e.target.checked)
                                                }
                                                required={field.required}
                                                disabled={field.disabled || formState.isSubmitting}
                                            />
                                            <span>
                                          {field.label}
                                        </span>
                                        </Stack>
                                    );
                                case 'checkbox':
                                    return (
                                        <Stack key={`${field.name}-${index}`} direction="row" sx={{ alignItems: 'center' }}>
                                            <Checkbox
                                                checked={formData[field.name] || false}
                                                onChange={(e) =>
                                                    handleChange(field.name, e.target.checked)
                                                }
                                                required={field.required}
                                                disabled={field.disabled || formState.isSubmitting}
                                                disableRipple
                                            />
                                            <span>
                                          {field.label}
                                        </span>
                                        </Stack>
                                    );
                                case 'separator':
                                    return (
                                        <Divider key={`${field.name}-${index}`}>
                                            <Typography variant="h4">{field.label}</Typography>
                                        </Divider>
                                    );
                                default:
                                    return null;
                            }
                        })}
                    </Stack>
                ))}
                {formState.success == false && (formState.message || formState.error) && (
                    <ErrorBox error={(formState.message || formState.error) ?? undefined} />
                )}
                <Stack direction="row" justifyContent="flex-end" spacing={2}>
                    {onCancel &&
                        <AbortButton onCancel={onCancel}/>
                    }
                    {onSubmit && !disableSaveAndClose &&
                        <FormSubmitButton
                            purpose={purpose}
                            formState={formState}
                            onSubmit={() => onSubmit(true)}
                        />
                    }
                    {onSubmit &&
                        <FormSubmitButton
                            purpose={purpose}
                            formState={formState}
                            onSubmit={() => onSubmit(false)}
                        />
                    }
                </Stack>
            </Stack>
        </form>
    );
};

const AbortButton = ({ onCancel }: { onCancel: () => void }) => (
    <Button
        variant="outlined"
        color="error"
        startIcon={<Cancel />}
        onClick={(e) => {
            e.preventDefault();
            onCancel();
        }}
    >
        Abbrechen
    </Button>
)
